<template>
    <div class="page">
        <div
            class="images fadeIn"
            :style="`
                --aspect-ratio-d:${getSize('single').d.join('/')};
                --aspect-ratio-m:${getSize('single').m.join('/')} `">
            <swiper
                v-if="croppedImgs"
                :options="croppedImgs.length > 1 ? heroSliderOptions : heroSliderOptionsWithOneSlide">
                <swiper-slide v-for="(image, index) in croppedImgs" :key="index">
                    <component
                        :is="
                            image.links.length === 1
                                ? image.links[0].url.startsWith('http')
                                    ? 'a'
                                    : 'NuxtLink'
                                : 'div'
                        "
                        :to="!image.links[0].url.startsWith('http') ? image.links[0].url : null"
                        :href="image.links[0].url.startsWith('http') ? image.links[0].url : null"
                        aria-label=""
                        draggable="false"
                        class="image-slide"
                        :aria-labelledby="`#slider-text-${index}`">
                        <div v-if="image.video.hls">
                            <HlsVideo
                                style="width: 100vw"
                                :loop="image.repeat_video || null"
                                v-lazy-load :data-poster="image.src"
                                :path="image.video.hls"></HlsVideo>
                            <div
                                v-if="image.text && !image.timer"
                                :id="`slider-text-${index}`"
                                class="image__title"
                                v-html="image.text"></div>
                        </div>
                        <div v-else-if="image.video.mp4 || image.video.webm">
                            <video
                                playsinline
                                muted
                                autoplay
                                :loop="image.repeat_video || null"
                                v-lazy-load :data-poster="image.src"
                                @ended="image.finished = true"
                                data-not-lazy>
                                <source v-lazy-load :data-src="image.video.mp4" type="video/mp4" data-not-lazy />
                                <source v-lazy-load :data-src="image.video.webm" type="video/webm" data-not-lazy />
                            </video>
                            <div
                                v-if="image.finished && !image.repeat_video"
                                @click.prevent.stop="doPlaySliderVideo"
                                class="image__repeat">
                                <svg-icon name="arrow-circle" width="24" height="24"></svg-icon>
                            </div>
                            <div
                                v-if="image.text && !image.timer"
                                :id="`slider-text-${index}`"
                                class="image__title"
                                v-html="image.text"></div>
                        </div>
                        <div class="image__imgs" v-else>
                            <template v-if="$device.isDesktop">
                                <UiImage
                                    v-if="image.src"
                                    :path="image.src"
                                    :mobile="{ width: image.dimensions.m[0], height: image.dimensions.m[1] }"
                                    :desktop="{
                                        width: image.dimensions.d[0],
                                        height: image.dimensions.d[1],
                                    }"></UiImage>
                                <UiImage
                                    v-if="image.src2"
                                    :path="image.src2"
                                    :mobile="{ width: image.dimensions.m[0], height: image.dimensions.m[1] }"
                                    :desktop="{
                                        width: image.dimensions.d[0],
                                        height: image.dimensions.d[1],
                                    }"></UiImage>
                            </template>
                            <template v-else>
                                <UiImage
                                    v-if="image.srcMobile"
                                    :path="image.srcMobile"
                                    :mobile="{ width: image.dimensions.m[0], height: image.dimensions.m[1] }"
                                    :desktop="{
                                        width: image.dimensions.d[0],
                                        height: image.dimensions.d[1],
                                    }"></UiImage>
                                <UiImage
                                    v-if="image.src2Mobile"
                                    :path="image.src2Mobile"
                                    :mobile="{ width: image.dimensions.m[0], height: image.dimensions.m[1] }"
                                    :desktop="{
                                        width: image.dimensions.d[0],
                                        height: image.dimensions.d[1],
                                    }"></UiImage>
                            </template>
                            <div v-if="!image.timer" class="image__content">
                                <div v-if="image.text" class="image__title" v-html="image.text"></div>
                                <div v-if="image.links.length" class="image__buttons">
                                    <component
                                        :is="link.url ? (link.url.startsWith('http') ? 'a' : 'NuxtLink') : 'div'"
                                        :to="!link.url.startsWith('http') ? link.url : null"
                                        :key="link.url"
                                        v-for="link in image.links"
                                        :href="link.url.startsWith('http') ? link.url : null"
                                        class="image__button button button--tertiary text_13">
                                        {{ link.text }}
                                    </component>
                                </div>
                            </div>
                        </div>
                        <countdown v-if="image.timer" :end-time="image.timer" :auto-start="true" class="countdown">
                            <template #process="{ timeObj }">
                                <div class="countdown__wrapper">
                                    <div class="countdown__main">
                                        <div class="text_19 countdown__title" v-html="image.text"></div>
                                        <div class="countdown__timer">
                                            <div v-if="timeObj.ceil.d - 1 > 0" class="countdown__time">
                                                <span class="countdown__time-top">
                                                    {{ timeObj.d }}
                                                </span>
                                                <span class="countdown__time-bottom">{{ $t("count_down_day") }}</span>
                                            </div>
                                            <div v-if="timeObj.ceil.h - 1 > 0" class="countdown__time">
                                                <span class="countdown__time-top">
                                                    {{ timeObj.h }}
                                                </span>
                                                <span class="countdown__time-bottom">
                                                    {{ $t("count_down_hour") }}
                                                </span>
                                            </div>
                                            <div v-if="timeObj.ceil.m - 1 > 0" class="countdown__time">
                                                <span class="countdown__time-top">
                                                    {{ timeObj.m }}
                                                </span>
                                                <span class="countdown__time-bottom">{{
                                                    $t("count_down_minute")
                                                }}</span>
                                            </div>
                                            <div v-if="timeObj.ceil.s - 1 > 0" class="countdown__time">
                                                <span class="countdown__time-top">
                                                    {{ timeObj.s }}
                                                </span>
                                                <span class="countdown__time-bottom">{{
                                                    $t("count_down_second")
                                                }}</span>
                                            </div>
                                        </div>
                                        <div v-if="image.links.length" class="countdown__buttons flex-c">
                                            <component
                                                :is="
                                                    link.url ? (link.url.startsWith('http') ? 'a' : 'NuxtLink') : 'div'
                                                "
                                                :to="!link.url.startsWith('http') ? link.url : null"
                                                :key="link.url"
                                                v-for="link in image.links"
                                                :href="link.url.startsWith('http') ? link.url : null"
                                                class="countdown__btn button button--tertiary text_13">
                                                {{ link.text }}
                                            </component>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </countdown>
                    </component>
                </swiper-slide>
            </swiper>
        </div>
        <ProductBlock
            class="index-block"
            v-for="(block, index) in blocks"
            :key="`${index}-d`"
            :block="block"></ProductBlock>

        <div class="index-block">
            <Insta></Insta>
        </div>
    </div>
</template>
<script>
import Product from "@/components/Product";
import Insta from "@/components/Insta";
import { mapGetters } from "vuex";
import { Swiper, SwiperSlide } from "@/plugins/swiper.client";
import UiImage from "@/components/ui/Image";
import HlsVideo from "@/components/ui/hls-video.vue";
import ProductBlock from "@/components/product-blocks/CommonBlock";
export default {
    name: "index",
    components: { Product, Insta, Swiper, SwiperSlide, UiImage, HlsVideo, ProductBlock },
    async asyncData({ $axios }) {
        const requests = [$axios.get("/api/slides")];

        let [images] = await Promise.all(requests);

        return {
            images: images.data,
        };
    },
    head() {
        return {
            title: this.$i18n.t("pps_title"),
            meta: [
                {
                    hid: "description",
                    name: "description",
                    content: this.$i18n.t("index_text"),
                },
                {
                    hid: "og:description",
                    name: "og:description",
                    content: this.$i18n.t("index_text"),
                },
            ],
        };
    },
    data() {
        return {
            croppedImgs: null,
            selectedItems: [],
            newItems: [],

            heroSliderOptions: {
                slidesPerView: 1,

                speed: 600,
                autoplay: {
                    delay: 5500,
                    disableOnInteraction: false,
                },
            },
            heroSliderOptionsWithOneSlide: {
                slidesPerView: 1,
                touchRatio: 0,
                allowTouchMove: false,
                noSwiping: true,
                simulateTouch: false,
            },
        };
    },
    watch: {
        async locale() {
            await this.$nuxt.refresh();
            this.$store.commit("crumbs/change", this.crumbs);
        },
    },
    computed: {
        ...mapGetters({
            locale: "globalvars/getLang",
            blocks: "globalvars/getProductBlocksForMain",
        }),
    },
    methods: {
        doPlaySliderVideo(event) {
            const video = event.target.previousElementSibling;
            if (video) {
                video.play();
            }
        },
        getSize(size) {
            return {
                d: size === "single" ? [1624, 650] : [812, 650],
                m: [414, 550],
            };
        },
    },
    async fetch() {
        if (this.$route.query.token) {
            this.$auth
                .loginWith("cookie", {
                    data: { token: this.$route.query.token },
                })
                .then((res) => {
                    this.$gtm.push({
                        event: "GAevent",
                        eventCategory: "user",
                        eventAction: "send",
                        eventLabel: "entrance",
                        userId: res.data.data.retail_id,
                    });
                    this.$router.push("/");
                })
                .catch((error) => {
                    console.log(`error =>`, error.response.data);
                });
        }
    },
    mounted() {
        if (this.images.length > 0) {
            this.croppedImgs = this.images.filter(Boolean).map((item) => {
                return {
                    repeat_video: item.repeat_video,
                    finished: false,
                    links: item.links,
                    text: item.text,
                    src: item.image.path,
                    srcMobile: item.mobile_image?.path || null,
                    src2: item.second_image?.path || null,
                    src2Mobile: item.second_mobile_image?.path || null,
                    dimensions: this.getSize(item.size),
                    size: item.size,
                    timer: item.timer,
                    video: {
                        mp4:
                            this.$device.isDesktop && item.video_mp4
                                ? item.video_mp4.path
                                : !this.$device.isDesktop && item.video_mp4_mobile
                                ? item.video_mp4_mobile.path
                                : null,
                        hls:
                            this.$device.isDesktop && item.video_mp4_playlist
                                ? item.video_mp4_playlist
                                : !this.$device.isDesktop && item.video_mp4_mobile_playlist
                                ? item.video_mp4_mobile_playlist
                                : null,
                        webm:
                            this.$device.isDesktop && item.video_webm
                                ? item.video_webm.path
                                : !this.$device.isDesktop && item.video_webm_mobile
                                ? item.video_webm_mobile.path
                                : null,
                    },
                };
            });
        }
    },
};
</script>
<style lang="less">
@media @sm {
    .swiper-container .splide__arrow {
        transform: translateY(-58px);
    }
}

.image__title {
    a {
        display: inline-flex;
        align-items: center;
        font-size: 25px;
        &:after {
            content: "";
            display: block;
            width: 40px;
            height: 15px;
            margin-left: 15px;
            background: url(../assets/icons/arrow-right.svg) center left / contain no-repeat;
        }
        &:hover {
            color: #000 !important;
            opacity: 0.8;
        }
        * {
            margin-bottom: 0;
        }
    }
    span[style*="255"] {
        a {
            &:after {
                filter: invert(1);
                -webkit-filter: invert(1);
            }
            &:hover {
                color: #fff !important;
                opacity: 0.8;
            }
        }
    }
    strong {
        font-weight: 700;
    }
    em {
        font-style: italic;
    }
}

.index-block {
    &__native-slider {
        overflow-x: scroll;
        display: flex;

        .product {
            width: 50vw;
        }
    }
    .swiper-container:not(.swiper-container-initialized) {
        .swiper-wrapper {
            display: flex;
            overflow: hidden;
            width: 100%;
            .swiper-slide {
                width: calc(100% / var(--slides)) !important;
            }
        }
    }
}
.index-block__title.text_13.red-title {
    color: #ff161a;
}
</style>
<style scoped lang="less">
.page {
    margin-top: -36px;
}
.image__repeat {
    position: absolute;
    left: 0;
    bottom: 0;
    padding: 8px;
    opacity: 0.2;
    cursor: pointer;
    color: #fff;
    &:hover {
        opacity: 1;
    }
    svg {
        pointer-events: none;
    }
}
.images {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    opacity: 0;
    aspect-ratio: var(--aspect-ratio-d);
    @media @sm {
        aspect-ratio: var(--aspect-ratio-m);
    }

    .text_11 {
        position: absolute;
        width: 100%;
        top: 20px;
        text-align: center;
        z-index: 0;
    }

    .swiper-container {
        width: 100%;
    }
    .swiper-slide {
        width: 100% !important;
        overflow: hidden;
        position: relative;
        .hls-container {
            aspect-ratio: 1.7777;
        }
        @media @sm {
            .hls-container {
                aspect-ratio: 0.75;
            }
        }
    }
    picture,
    img,
    video {
        width: 100vw;
        object-fit: cover;
        display: block;
    }
}
.image {
    width: 50%;
    position: relative;
    padding-top: 53%;
    overflow: hidden;
    z-index: 1;
    &:hover {
        .image__title:after {
            transform: translate(15px);
        }
    }
    @media @sm {
        padding-top: 0;
        width: 100%;
    }

    &__content {
        position: absolute;
        bottom: 30px;
        left: 32px;
        right: 32px;
        z-index: 2;
    }
    &__buttons {
        margin-top: 16px;
        display: flex;
        gap: 16px;
        justify-content: center;
        flex-wrap: wrap;
    }

    &__title {
        font-weight: 300;
        font-size: 25px;
        line-height: 100%;
        /* identical to box height, or 25px */
        letter-spacing: 0.03em;
        display: flex;
        align-items: center;
        @media @sm {
            font-size: 18px;
        }
        &:after {
            content: "";
            margin-left: 14px;
            display: inline-block;
            vertical-align: middle;
            width: 40px;
            height: 22px;
            .trs(transform);
            background: url(../assets/icons/arrow-right.svg?inline) center left / contain no-repeat;
            filter: invert(1);
        }
    }
    &--double {
        width: 100%;
    }
    &__imgs {
        position: relative;
        &:before {
            content: "";
            position: absolute;
            left: 0px;
            top: 0px;
            width: 100%;
            height: 100%;
            background: linear-gradient(180deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0.075) 100%);
            z-index: 1;
        }
        .image-slide,
        picture {
            display: block;
            width: 100vw;
            img {
                width: 100%;
                object-fit: cover;
            }
        }
        picture + picture {
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            position: absolute;
            opacity: 0;
            .trs(opacity, 0.3s, 3s);
            .fadeIn & {
                opacity: 1;
                transform: translateZ(0);
                backface-visibility: hidden;
                animation: slowFlash 6s 3s ease infinite;
            }
        }
    }
    &__holder {
        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        bottom: 0;
        @media @sm {
            position: static;
        }
    }
}
@keyframes slowFlash {
    0%,
    20% {
        opacity: 0;
    }
    28%,
    70% {
        opacity: 1;
    }
    78%,
    100% {
        opacity: 0;
    }
}

.countdown {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    z-index: 1;
    &:after {
        content: "";
        position: absolute;
        width: 1039px;
        height: 414px;
        left: 50%;

        top: calc(100% - 120px);
        margin-left: -520px;
        margin-top: -207px;
        border-radius: 50%;
        z-index: 1;
        background: radial-gradient(rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 70%);
    }

    &__wrapper {
        position: relative;
        z-index: 2;
        padding: 32px;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        height: 100%;
        color: white;
        @media @sm {
            padding: 16px;
        }
    }
    &__main {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        flex-grow: 1;
        padding-bottom: 6%;
        @media @sm {
            padding-bottom: 30%;
        }
    }
    &__title {
        text-align: center;
        max-width: 100vw;
        padding: 0 10px;
        @media @sm {
            font-size: 15px;
            line-height: 140%;
        }
    }
    &__buttons {
        gap: 16px;
    }
    &__btn {
        margin-top: 32px;

        @media @sm {
            margin-top: 16px;
        }
    }
    &__timer {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        margin-top: 16px;
        gap: 32px;
        @media @sm {
            margin-top: 4px;
            gap: 16px;
        }
    }
    &__time {
        display: flex;
        align-items: flex-start;
        justify-content: center;
        position: relative;
        gap: 4px;
        @media @sm {
            width: 25%;
        }
        &-top {
            font-weight: 100;
            font-size: 88px;
            line-height: 100%;
            letter-spacing: 0.16em;
            @media @sm {
                font-size: 30px;
                font-weight: 400;
            }
        }
        &-bottom {
            font-weight: 400;
            font-size: 19px;
            line-height: 140%;
            letter-spacing: 0.16em;
            @media @sm {
                font-size: 13px;
                line-height: 140%;
                letter-spacing: 0.1em;
            }
        }
    }
}
</style>
<style>
.is_desktop .slider_hero .splide__slide:nth-child(2n) {
    left: -1px;
}
</style>
